import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Activity, X, AlertTriangle, DollarSign, Users, BarChart2 } from "lucide-react";

export default function DryRunModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  React.useEffect(() => {
    if (!open) return;
    setBusy(true); setErr("");
    api.get("/billing/admin/dry-run")
      .then(r => setData(r.data))
      .catch(e => setErr(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setBusy(false));
  }, [open]);

  if (!open) return null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 70, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: 40, overflowY: "auto" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 1100, background: "var(--navy-800)", border: "1px solid var(--line)", borderRadius: 6 }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div className="font-mono" style={{ fontSize: 11, color: "var(--amber-400)", letterSpacing: ".15em" }}>PRE-LAUNCH SIMULATION</div>
            <div className="font-serif-display" style={{ fontSize: 24, fontWeight: 600, marginTop: 4 }}>
              <Activity size={18} style={{ display: "inline", verticalAlign: -3, marginRight: 8, color: "var(--amber-500)" }} />
              Billing Dry-Run
            </div>
            <div style={{ fontSize: 13, color: "var(--ink-dim)", marginTop: 4 }}>What would happen if all monetization flags were ON right now? Read-only — nothing is recorded.</div>
          </div>
          <button className="btn btn-ghost" onClick={onClose} style={{ padding: 8 }}><X size={16} /></button>
        </div>

        <div style={{ padding: 24 }}>
          {busy && <div style={{ color: "var(--ink-mute)" }}>Simulating…</div>}
          {err && <div style={{ color: "var(--red)" }}>{err}</div>}
          {data && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 22 }}>
                <Tile icon={DollarSign} label="Commissions if launched" value={`$${data.summary.would_record_commissions_total_cad.toLocaleString()}`} sub={`${data.summary.commission_rate_pct}% of ${data.summary.orders_in_scope} closed orders`} accent="var(--amber-500)" />
                <Tile icon={Users} label="Suppliers in scope" value={data.summary.suppliers_in_scope} sub={`${data.summary.suppliers_without_active_subscription} without active subscription`} />
                <Tile icon={AlertTriangle} label="Over Quota This Month" value={data.summary.suppliers_over_quota_this_month} sub="would be blocked from quoting" />
                <Tile icon={BarChart2} label="Quotas (Free/Pro/Ent)" value={`${data.summary.rfq_quotas.free}/${data.summary.rfq_quotas.pro}/${data.summary.rfq_quotas.enterprise}`} sub="quotes per month" />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                <Section title="COMMISSIONS BY SUPPLIER">
                  {data.commissions_by_supplier.length === 0 ? <Empty msg="No closed orders yet — no commissions to record." /> : (
                    <table className="data" style={{ fontSize: 13 }}>
                      <thead><tr><th>Supplier</th><th>Plan</th><th>Orders</th><th>Revenue</th><th>Commission</th></tr></thead>
                      <tbody>
                        {data.commissions_by_supplier.map((r, i) => (
                          <tr key={i}>
                            <td>{r.supplier_name}</td>
                            <td><span className="pill" style={{ borderColor: "var(--line)", color: "var(--amber-400)" }}>{r.current_plan.toUpperCase()}</span></td>
                            <td>{r.orders}</td>
                            <td>${r.revenue.toLocaleString()}</td>
                            <td style={{ fontWeight: 600, color: "var(--amber-500)" }}>${r.commission.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Section>
                <Section title="QUOTA USAGE THIS MONTH">
                  {data.quota_usage.length === 0 ? <Empty msg="No suppliers." /> : (
                    <table className="data" style={{ fontSize: 13 }}>
                      <thead><tr><th>Supplier</th><th>Plan</th><th>Used / Quota</th><th>Status</th></tr></thead>
                      <tbody>
                        {data.quota_usage.map(r => (
                          <tr key={r.supplier_id} style={{ background: r.would_block ? "rgba(239,68,68,.06)" : undefined }}>
                            <td>{r.supplier_name}</td>
                            <td><span className="pill" style={{ borderColor: "var(--line)", color: "var(--amber-400)" }}>{r.plan.toUpperCase()}</span></td>
                            <td className="font-mono" style={{ color: r.over_quota ? "var(--red)" : "var(--ink-dim)" }}>{r.quotes_this_month} / {r.quota}</td>
                            <td>
                              {r.would_block && <span className="pill pill-expired">WOULD BLOCK</span>}
                              {!r.would_block && r.manual_override && <span className="pill pill-accepted">OVERRIDE</span>}
                              {!r.would_block && !r.manual_override && r.tenant_enabled === false && <span className="pill" style={{ color: "var(--ink-dim)", borderColor: "var(--line)" }}>EXEMPT</span>}
                              {!r.would_block && !r.manual_override && r.tenant_enabled !== false && <span className="pill pill-accepted">OK</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Section>
              </div>

              <div style={{ marginTop: 24, padding: 14, background: "var(--navy-900)", border: "1px dashed var(--line)", borderRadius: 4 }}>
                <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)", letterSpacing: ".1em", marginBottom: 6 }}>CURRENT FLAG STATE</div>
                <div className="font-mono" style={{ fontSize: 12, color: "var(--ink-dim)", display: "flex", flexWrap: "wrap", gap: 14 }}>
                  {Object.entries(data.current_flags).map(([k, v]) => (
                    <span key={k}><span style={{ color: "var(--ink-mute)" }}>{k}=</span><span style={{ color: v ? "var(--emerald)" : "var(--amber-400)" }}>{String(v)}</span></span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--ink-mute)", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 600 }}>
        <Icon size={12} /> {label}
      </div>
      <div className="font-serif-display" style={{ fontSize: 26, color: accent || "var(--ink)", marginTop: 8, fontWeight: 600 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "var(--ink-mute)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="card">
      <div className="font-mono" style={{ padding: "12px 16px", borderBottom: "1px solid var(--line)", fontSize: 11, color: "var(--ink-mute)", letterSpacing: ".1em" }}>{title}</div>
      <div style={{ overflowX: "auto" }}>{children}</div>
    </div>
  );
}

function Empty({ msg }) {
  return <div style={{ padding: 24, textAlign: "center", color: "var(--ink-mute)", fontSize: 13 }}>{msg}</div>;
}
